import { useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
// helpers
import { getLdwExcessDisplay } from "../helpers/getLdwExcessDisplay";
// components
import EuropcarInsuranceDetailsModal from "../EuropcarInsuranceDetailsModal";

/**
 * Insurance section shown above Driver and Vehicle Details.
 * Clicking the info icon next to title "Basic" opens a details modal.
 */

type EuropcarInsuranceSectionProps = {
  selectedCar?: any;
};

export default function EuropcarInsuranceSection({ selectedCar }: EuropcarInsuranceSectionProps) {
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const { t } = useTranslation();
  const ldwExcessDisplay = getLdwExcessDisplay(selectedCar);

  return (
    <Box mb={4} mt={2} className="europcar-insurance-section">
      <Box sx={{ fontFamily: "Neutraface2Demi", fontSize: { xs: 14, sm: 15 }, mb: 2, textTransform: "uppercase" }}>
        {t("transport.europcarInsurance.sectionTitle")}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 4 },
          alignItems: { xs: "flex-start", sm: "flex-start" }
        }}
      >
        {/* Left: plan summary */}
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: { sm: 140 }, gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box component="span" sx={{ fontFamily: "Neutraface2Demi", fontSize: 15 }}>
              {t("transport.europcarInsurance.planName")}
            </Box>
            <IconButton
              onClick={() => setDetailsModalOpen(true)}
              aria-label={t("transport.europcarInsurance.viewPlanDetailsAriaLabel")}
              size="small"
              sx={{ p: 0.25, color: "text.secondary" }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box sx={{ fontSize: { xs: 12, sm: 14 }, color: "text.secondary" }}>
            {t("transport.europcarInsurance.excessLabel")}: {ldwExcessDisplay}
          </Box>
          <Box sx={{ fontFamily: "Neutraface2Demi", fontSize: { xs: 12, sm: 14 } }}>
            {t("transport.europcarInsurance.statusIncluded")}
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: "none", sm: "block" },
            alignSelf: "stretch"
          }}
        />

        {/* Right: coverage list */}
        <Box sx={{ flex: 1 }}>
          {[
            { label: t("transport.europcarInsurance.coverage.collisionDamageAndTheft"), included: true },
            { label: t("transport.europcarInsurance.coverage.windscreenGlassLightsTires"), included: false },
            { label: t("transport.europcarInsurance.coverage.personalAccident"), included: false }
          ].map((item, index, arr) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: { xs: 12, sm: 14 },
                mb: index < arr.length - 1 ? 1 : 0
              }}
            >
              {item.included ? (
                <CheckIcon sx={{ fontSize: 18, color: "success.main" }} />
              ) : (
                <CloseIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              )}
              <span>{item.label}</span>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ fontSize: { xs: 13, sm: 15 }, color: "text.primary", mt: 3 }}>
        {t("transport.europcarInsurance.upgradeNote")}
      </Box>

      <EuropcarInsuranceDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        ldwExcessDisplay={ldwExcessDisplay}
      />
    </Box>
  );
}
